import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { workoutService } from '../services/workoutService';
import { useAuth } from './AuthContext';

const WorkoutContext = createContext({
  workout: null,
  workoutProgress: null,
  exercisesProgress: [],
  loading: false,
  error: null,
  refreshCurrentWorkout: async () => {},
  setCurrentWorkoutState: () => {}
});

export const WorkoutProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [workout, setWorkout] = useState(null);
  const [workoutProgress, setWorkoutProgress] = useState(null);
  const [exercisesProgress, setExercisesProgress] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasFetchedRef = useRef(false);

  const resetState = useCallback(() => {
    setWorkout(null);
    setWorkoutProgress(null);
    setExercisesProgress([]);
    setError(null);
    setLoading(false);
  }, []);

  const fetchCurrentWorkout = useCallback(async () => {
    if (!isAuthenticated) {
      resetState();
      return;
    }

    setLoading(true);
    try {
      const data = await workoutService.getCurrentWorkout();
      setWorkout(data?.workout ?? null);
      setWorkoutProgress(data?.workoutProgress ?? null);
      setExercisesProgress(data?.exercisesProgress ?? []);
      setError(null);
    } catch (err) {
      console.error('Error loading current workout', err);
      setWorkout(null);
      setWorkoutProgress(null);
      setExercisesProgress([]);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, resetState]);

  useEffect(() => {
    if (!isAuthenticated) {
      resetState();
      hasFetchedRef.current = false;
      return;
    }
    if (hasFetchedRef.current) {
      return;
    }
    hasFetchedRef.current = true;
    void fetchCurrentWorkout();
  }, [isAuthenticated, fetchCurrentWorkout, resetState]);

  const value = useMemo(
    () => ({
      workout,
      workoutProgress,
      exercisesProgress,
      loading,
      error,
      refreshCurrentWorkout: fetchCurrentWorkout,
      setCurrentWorkoutState: ({ workout: newWorkout, workoutProgress: newProgress, exercisesProgress: newExercises = [] }) => {
        setWorkout(newWorkout ?? null);
        setWorkoutProgress(newProgress ?? null);
        setExercisesProgress(newExercises ?? []);
      }
    }),
    [workout, workoutProgress, exercisesProgress, loading, error, fetchCurrentWorkout]
  );

  return <WorkoutContext.Provider value={value}>{children}</WorkoutContext.Provider>;
};

export const useCurrentWorkout = () => useContext(WorkoutContext);
